import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo } from 'react'
import { filterNullish, type ChargeType } from '@sevenrooms/api'
import {
  getShouldTimeSlotShowCost,
  type AvailabilityRangeRequest,
  type AvailabilityTime,
  type GuestFacingUpgrade,
  type ExperienceAvailabilitySingleDay,
  PDRAmenitiesEnum,
  PDRRoomTypeEnum,
  PDRUnitOfMeasurementEnum,
  type OfferType,
} from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { TimeOnly } from '@sevenrooms/timepiece'
import {
  useGetSingleDayAvailabilityQuery,
  useGetRangeAvailabilityQuery,
  useGetGuestFacingUpgradeQuery,
  useGetExperiencesAvailabilitySingleDayQuery,
} from '../store'
import {
  calculateCategoryFees,
  calculateReservationFees,
  getImageURlFromPhotoDict,
  getMenuFromExperience,
  sortPDRExperiences,
  getTotalMaxCapacity,
  getTotalMinCapacity,
  defaultExperiencesSort,
} from '../utils'
import { useReservationsRoute } from './useReservationsRoute'
import { useVenue } from './useVenue'
import { useWidgetSettings } from './useWidgetSettings'

// per duration costs are based on 15 minute intervals
const DURATION_15_MINUTES = 15

export type AvailabilityTimeWithUpSellCost = AvailabilityTime & {
  includedUpgradesCost: number
  showCost: boolean
  fees: number | undefined
}

export type ExperienceWithAvailability = ReturnType<typeof toExperience> & {
  requestTimeRange?: string[]
  reservationTimesInHalo?: AvailabilityTimeWithUpSellCost[]
  reservationTimes?: AvailabilityTimeWithUpSellCost[]
}

export function useSingleDayAvailability({
  venueId,
  startTime,
  haloTimeIntervalMinutes,
  startOfDayTime,
  skipRequests,
  ...queryParams
}: Omit<AvailabilityRangeRequest, 'numDays'> & {
  venueId: string
  startTime: string
  haloTimeIntervalMinutes: number
  startOfDayTime: string
} & { skipRequests: boolean }) {
  const { minGuests, isFeesInPriceDisplayed, defaultSearchTime } = useWidgetSettings()
  const { venueLanguages, venueToday, id: pageVenueId } = useVenue()
  const cleanQueryParams = useMemo(() => {
    if (venueId !== pageVenueId) {
      return queryParams
    }
    const defaultVenueLanguage = venueLanguages.find(({ isDefault }) => isDefault)?.value ?? 'en'
    const cleanQueryParams = { ...queryParams }
    if (cleanQueryParams.selectedLangCode === defaultVenueLanguage) {
      delete cleanQueryParams.selectedLangCode
    }
    if (cleanQueryParams.timeSlot === defaultSearchTime) {
      delete cleanQueryParams.timeSlot
    }
    if (cleanQueryParams.startDate === venueToday) {
      delete cleanQueryParams.startDate
    }
    return cleanQueryParams
  }, [pageVenueId, venueId, queryParams, defaultSearchTime, venueLanguages, venueToday])
  const {
    isFetching: isAvailabilityFetching,
    data: availabilityData,
    error,
  } = useGetSingleDayAvailabilityQuery(
    !skipRequests ? cleanQueryParams : skipToken,
    // This Param indicates that the if we have a newly mounted hook,
    //    and the args do not change and we have a valid response from the server thats less than 5 seconds old
    //    We will use the cached version of the response, this is primarily to maintain the optimization from @/VenueDataLoader
    { refetchOnMountOrArgChange: 5 }
  )
  const { isFetching: isUpgradesFetching, data: upgradesData } = useGetGuestFacingUpgradeQuery(!skipRequests ? venueId : skipToken)
  const data = useMemo(() => {
    if (!availabilityData || !upgradesData) {
      return undefined
    }
    const updatedAvailabilityTimes = availabilityData.availabilityTimes.map(timeSlot =>
      getTimeslotIncludedUpsellsCost(timeSlot, upgradesData, cleanQueryParams.partySize ?? minGuests, isFeesInPriceDisplayed)
    )
    return {
      ...availabilityData,
      availabilityTimes: updatedAvailabilityTimes,
    }
  }, [availabilityData, upgradesData, cleanQueryParams.partySize, isFeesInPriceDisplayed, minGuests])

  const filteredData = useMemo(() => {
    if (!data) {
      return data
    }
    const filteredAvailabilityTimes =
      startTime === ReservationWidget.AllTimesOption
        ? data.availabilityTimes
        : filterTimeSlotsByHaloTimeInterval(data.availabilityTimes, startTime, haloTimeIntervalMinutes, startOfDayTime)
    return {
      ...data,
      reservationTimesInHalo: getReservationTimesFromAvailability(filteredAvailabilityTimes),
      reservationTimes: getReservationTimesFromAvailability(data.availabilityTimes),
      requestTimeRange: getRequestableTimesFromAvailability({ availabilityTimes: filteredAvailabilityTimes }),
    }
  }, [data, haloTimeIntervalMinutes, startOfDayTime, startTime])

  const isClosed = useMemo(() => {
    if (!availabilityData) {
      return false
    }

    return availabilityData.shiftData.length === 0 || availabilityData.shiftData.every(shift => shift.isClosed)
  }, [availabilityData])

  return {
    isFetching: isAvailabilityFetching || isUpgradesFetching,
    isClosed,
    data: filteredData,
    errorCode: error as number | undefined,
  }
}

export function useExperiencesWithAvailability({
  venue,
  partySize,
  startDate,
  skipRequests,
  clientId,
  selectedLangCode,
  startTime,
  haloTimeIntervalMinutes,
  startOfDayTime,
  experienceType,
}: {
  venue: string
  partySize?: number
  startDate?: string
  skipRequests?: boolean
  clientId?: string
  selectedLangCode?: string
  startTime?: string
  haloTimeIntervalMinutes?: number
  startOfDayTime?: string
  experienceType: OfferType
}) {
  const { defaultSearchTime, isFeesInPriceDisplayed } = useWidgetSettings()
  const { channel } = useReservationsRoute()
  const isPrivateEventsExperience = experienceType === 'PDR'
  const [partySizeToFetch, startDateToFetch] = useMemo(() => {
    // don't fetch data if partySize or startDate are not selected
    if ((!partySize || !startDate) && !(!partySize && !startDate)) {
      return [undefined, undefined]
    }
    return [partySize, startDate]
  }, [partySize, startDate])

  const { data: experiences, isFetching } = useGetExperiencesAvailabilitySingleDayQuery(
    skipRequests
      ? skipToken
      : {
          venue,
          partySize: partySizeToFetch,
          startDate: startDateToFetch,
          timeSlot: defaultSearchTime,
          channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
          haloSizeInterval: ReservationWidget.WholeDayHaloSizeInterval,
          clientId,
          selectedLangCode,
          error: '',
          experienceType,
        }
  )
  const { isFetching: isUpgradesFetching, data: upgradesData } = useGetGuestFacingUpgradeQuery(!skipRequests ? venue : skipToken)

  const data = useMemo(() => {
    if (!experiences || !upgradesData) {
      return undefined
    }

    return experiences
      .map(experience => {
        const offer = toExperience(experience, upgradesData, isFeesInPriceDisplayed, partySize)
        let filteredAvailabilities: AvailabilityTimeWithUpSellCost[] | undefined = offer.timeSlots
        if (
          startTime !== ReservationWidget.AllTimesOption &&
          !!offer.timeSlots?.length &&
          startTime !== undefined &&
          haloTimeIntervalMinutes !== undefined &&
          startOfDayTime !== undefined
        ) {
          filteredAvailabilities = filterTimeSlotsByHaloTimeInterval(offer.timeSlots, startTime, haloTimeIntervalMinutes, startOfDayTime)
        }
        return {
          ...offer,
          reservationTimesInHalo: filteredAvailabilities ? getReservationTimesFromAvailability(filteredAvailabilities) : undefined,
          reservationTimes: offer.timeSlots ? getReservationTimesFromAvailability(offer.timeSlots) : undefined,
          requestTimeRange: filteredAvailabilities
            ? getRequestableTimesFromAvailability({ availabilityTimes: filteredAvailabilities })
            : undefined,
        }
      })
      .sort(isPrivateEventsExperience ? sortPDRExperiences : defaultExperiencesSort)
  }, [
    experiences,
    haloTimeIntervalMinutes,
    isFeesInPriceDisplayed,
    isPrivateEventsExperience,
    partySize,
    startOfDayTime,
    startTime,
    upgradesData,
  ])

  return {
    isFetching: isFetching || isUpgradesFetching,
    data: skipRequests ? undefined : data,
  }
}

export function useMultiDayAvailability({
  venueId,
  shouldSkip,
  startTime,
  haloTimeIntervalMinutes,
  startOfDayTime,
  ...queryParams
}: AvailabilityRangeRequest & {
  venueId: string
  shouldSkip: boolean
  startTime: string
  haloTimeIntervalMinutes: number
  startOfDayTime: string
}) {
  const { isFetching: isAvailabilityFetching, data: availabilityData } = useGetRangeAvailabilityQuery(shouldSkip ? skipToken : queryParams)
  const { isFetching: isUpgradesFetching, data: upgradesData } = useGetGuestFacingUpgradeQuery(shouldSkip ? skipToken : venueId)
  const { isFeesInPriceDisplayed, minGuests } = useWidgetSettings()
  const data = useMemo(() => {
    if (!availabilityData || !upgradesData) {
      return undefined
    }
    return Object.keys(availabilityData)
      .map(availabilityDate => {
        const specificAvailability = availabilityData[availabilityDate]
        if (!specificAvailability) {
          return undefined
        }
        const { shiftData } = specificAvailability
        const updatedReservationTimes = specificAvailability.availabilityTimes.map(timeSlot =>
          getTimeslotIncludedUpsellsCost(timeSlot, upgradesData, queryParams.partySize ?? minGuests, isFeesInPriceDisplayed)
        )
        return { date: availabilityDate, timeslots: getReservationTimesFromAvailability(updatedReservationTimes), shiftData }
      })
      .filter(filterNullish)
  }, [availabilityData, upgradesData, queryParams.partySize, isFeesInPriceDisplayed, minGuests])

  const filteredData = useMemo(() => {
    if (!data || startTime === ReservationWidget.AllTimesOption) {
      return data
    }
    return data.map(availability => {
      const filteredReservationTimes = filterTimeSlotsByHaloTimeInterval(
        availability.timeslots,
        startTime,
        haloTimeIntervalMinutes,
        startOfDayTime
      )
      return {
        ...availability,
        timeslots: filteredReservationTimes,
      }
    })
  }, [data, haloTimeIntervalMinutes, startOfDayTime, startTime])

  return {
    isFetching: isAvailabilityFetching || isUpgradesFetching,
    data: shouldSkip ? undefined : filteredData,
  }
}

export function getRequestableTimesFromAvailability({ availabilityTimes }: { availabilityTimes: AvailabilityTimeWithUpSellCost[] }) {
  return availabilityTimes
    .filter(({ type, isRequestable }) => type === 'request' && isRequestable !== false)
    .sort((availabilityTime1, availabilityTime2) => (availabilityTime1.sortOrder > availabilityTime2.sortOrder ? 1 : -1))
    .map(({ timeIso }) => timeIso)
    .reduce<string[]>((acc, value) => {
      if (!acc.includes(value)) {
        acc.push(value)
      }
      return acc
    }, [])
}

export function getReservationTimesFromAvailability(availabilityTimes: AvailabilityTimeWithUpSellCost[]) {
  return availabilityTimes.filter(({ type }) => type === 'book').sort((t1, t2) => t1.sortOrder - t2.sortOrder)
}

export function filterTimeSlotsByHaloTimeInterval(
  reservationTimes: AvailabilityTimeWithUpSellCost[],
  startTime: string,
  haloTimeIntervalMinutes: number,
  startOfDayTime: string
) {
  if (!reservationTimes.length) {
    return reservationTimes
  }

  const startOfDayTimeOnly = TimeOnly.from(startOfDayTime).toJsDate()
  const selectedTime = TimeOnly.from(startTime).toJsDate()
  const startTimeToCompare = new Date(selectedTime.getTime())
  startTimeToCompare.setMinutes(startTimeToCompare.getMinutes() - haloTimeIntervalMinutes)
  const endTimeToCompare = new Date(selectedTime.getTime())
  endTimeToCompare.setMinutes(endTimeToCompare.getMinutes() + haloTimeIntervalMinutes)
  const isNextDay = selectedTime.getTime() < startOfDayTimeOnly.getTime()

  return reservationTimes.filter(time => {
    const resTime = TimeOnly.from(time.timeIso).toJsDate()
    const isResTimeSameDay = isNextDay
      ? resTime.getTime() < startOfDayTimeOnly.getTime()
      : resTime.getTime() >= startOfDayTimeOnly.getTime()
    const isResTimeBelow = resTime.getTime() <= endTimeToCompare.getTime()
    const isResTimeAbove = resTime.getTime() >= startTimeToCompare.getTime()
    if (!isResTimeBelow && resTime.getDate() !== startTimeToCompare.getDate()) {
      resTime.setDate(resTime.getDate() - 1)
      return resTime.getTime() >= startTimeToCompare.getTime()
    } else if (!isResTimeAbove && resTime.getDate() !== endTimeToCompare.getDate()) {
      resTime.setDate(resTime.getDate() + 1)
      return resTime.getTime() <= endTimeToCompare.getTime()
    }
    return isResTimeAbove && isResTimeBelow && isResTimeSameDay
  })
}

export function getTimeslotIncludedUpsellsCost(
  timeSlot: AvailabilityTime,
  upgradesData: GuestFacingUpgrade,
  partySize: number,
  isFeesInPriceDisplayed = false
) {
  const showCost = getShouldTimeSlotShowCost(
    timeSlot.requireCreditCard,
    timeSlot.ccPartySizeMin,
    partySize,
    (timeSlot.selectedAutomaticUpsells?.length ?? 0) > 0
  )

  if (!showCost) {
    return {
      ...timeSlot,
      includedUpgradesCost: 0,
      fees: 0,
      showCost,
    }
  }

  const includedUpgradesCost =
    timeSlot.selectedAutomaticUpsells
      ?.map(({ id: upgradeId, quantityEqualType, quantityNum }) => {
        const upsellData = upgradesData.inventories.find(inventory => inventory.id === upgradeId)
        if (!upsellData) {
          return undefined
        }
        const quantity = quantityEqualType === 'SPECIFIC_NUMBER' ? quantityNum : partySize
        const { price } = upsellData
        const category = upgradesData.categories.find(category => category.id === upsellData.categoryId)
        const fees = category ? calculateCategoryFees(price, category, timeSlot.defaultServiceCharge, timeSlot.defaultGratuity) : 0
        return quantity * (price + (isFeesInPriceDisplayed ? fees : 0))
      })
      .reduce((acc = 0, value = 0) => value + acc, 0) ?? 0

  return {
    ...timeSlot,
    includedUpgradesCost: getCostFromChargeType(includedUpgradesCost, timeSlot.chargeType, partySize, timeSlot.duration),
    fees: calculateReservationFees(isFeesInPriceDisplayed, timeSlot),
    showCost,
  }
}

function getCostFromChargeType(cost: number, chargeType: ChargeType | null | undefined, partySize: number, duration: number) {
  if (cost === 0) {
    return 0
  }
  switch (chargeType) {
    case 'person_slot':
      return cost / partySize / (duration / DURATION_15_MINUTES)
    case 'person':
      return cost / partySize
    case 'reservation_slot':
      return cost / (duration / DURATION_15_MINUTES)
    case 'reservation':
    default:
      return cost
  }
}

export function toExperience(
  experience: ExperienceAvailabilitySingleDay,
  upgradesData?: GuestFacingUpgrade,
  isFeesInPriceDisplayed?: boolean,
  partySize?: number
) {
  const availabilities =
    partySize !== undefined && upgradesData
      ? experience.availabilities?.availabilityTimes.map(timeSlot =>
          getTimeslotIncludedUpsellsCost(timeSlot, upgradesData, partySize, isFeesInPriceDisplayed)
        )
      : undefined

  return {
    id: experience.id,
    urlKey: experience.landingUrlKey,
    name: experience.name,
    offerType: experience.offerType,
    description: experience.description,
    timeSlots: availabilities,
    heroImage: getImageURlFromPhotoDict(experience.heroImage.photoDict),
    imageList: experience.imageList.map(image => getImageURlFromPhotoDict(image.photoDict)).filter(image => !!image),
    fees: experience.pdrFees,
    menu: getMenuFromExperience(experience),
    // eslint-disable-next-line no-warning-comments
    amenities: experience.pdrAmenityList.map(amenity => PDRAmenitiesEnum[amenity]), // TODO ADD GFL SUPPORT
    seatedCapacityMin: experience.pdrMinSeatedCapacity ?? undefined,
    seatedCapacityMax: experience.pdrMaxSeatedCapacity ?? undefined,
    standingCapacityMin: experience.pdrStandingMinCapacity ?? undefined,
    standingCapacityMax: experience.pdrStandingCapacity ?? undefined,
    sortOrder: experience.sortOrder,
    totalCapacityMin: getTotalMinCapacity(experience),
    totalCapacityMax: getTotalMaxCapacity(experience),
    minSpendMin: experience.pdrMinSpendMin ?? undefined,
    minSpendMax: experience.pdrMinSpendMax ?? undefined,
    // eslint-disable-next-line no-warning-comments
    roomType: experience.pdrRoomType ? PDRRoomTypeEnum[experience.pdrRoomType] : undefined, // TODO ADD GFL SUPPORT
    squareFootage: experience.pdrSquareFootage ?? undefined,
    // eslint-disable-next-line no-warning-comments
    // TODO ADD GFL SUPPORT
    squareFootageMeasurement: experience.pdrUnitOfMeasurement ? PDRUnitOfMeasurementEnum[experience.pdrUnitOfMeasurement] : undefined,
  }
}
